import { useMemo, useCallback } from "react";
import clsx from "clsx";
import { Tooltip } from "react-migration/components/Tooltip";
import { Theme } from "react-migration/lib/theme/Theme";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { PrintableLayerControls } from "./components/PrintableLayerControls";
import { TabLoadingState } from "./useReportLoadingState";
import { AssessmentTab as TabType } from "./types";
import { getGroupCheckedState, tabTranslationKey } from "./utils";

interface AssessmentTabProps {
  tab: TabType;
  loadingState: TabLoadingState;
  checkedState: Record<string, boolean>;
  handleToggleTab: (toggleParams: { tabKey: string; layerId?: string; groupId?: string }) => void;
  getCheckboxState?: (path: string) => boolean;
  toggleCheckbox?: (path: string) => void;
}

export function AssessmentTab({
  tab,
  loadingState,
  checkedState,
  handleToggleTab,
  getCheckboxState,
  toggleCheckbox,
}: Readonly<AssessmentTabProps>) {
  const { t } = useTranslation();

  const hiddenSectionIds = useCallback(
    (groupId: string, sectionIds: string[]) => {
      if (!getCheckboxState) return [];

      return sectionIds.filter((sectionId) => {
        const path = `${tab.key}.${groupId}.${sectionId}`;
        return !getCheckboxState(path);
      });
    },
    [getCheckboxState, tab.key]
  );

  const toggleSection = useCallback(
    (groupId: string) => {
      if (!toggleCheckbox) return undefined;

      return (sectionId: string) => {
        const path = `${tab.key}.${groupId}.${sectionId}`;
        toggleCheckbox(path);
      };
    },
    [toggleCheckbox, tab.key]
  );

  const allLayerIds = useMemo(() => tab.groups.flatMap((group) => group.layerIds), [tab.groups]);

  const tabCheckedState = useMemo(() => {
    if (!getCheckboxState) {
      return getGroupCheckedState(allLayerIds, checkedState);
    }

    const allTabPaths: string[] = [];
    tab.groups.forEach((group) => {
      if (group.sectionIds) {
        group.sectionIds.forEach((sectionId) => {
          allTabPaths.push(`${tab.key}.${group.id}.${sectionId}`);
        });
      } else {
        group.layerIds.forEach((layerId) => {
          allTabPaths.push(`${tab.key}.${layerId}`);
        });
      }
    });

    if (allTabPaths.length === 0) return false;

    const checkedCount = allTabPaths.filter((path) => getCheckboxState(path)).length;
    if (checkedCount === 0) return false;
    if (checkedCount === allTabPaths.length) return true;
    return "indeterminate";
  }, [tab, getCheckboxState, checkedState, allLayerIds]);

  const groupCheckedStates = useMemo(() => {
    const states: Record<string, boolean | "indeterminate"> = {};

    tab.groups.forEach((group) => {
      if (group.sectionIds && getCheckboxState) {
        const groupPaths = group.sectionIds.map((sectionId) => `${tab.key}.${group.id}.${sectionId}`);
        if (groupPaths.length === 0) {
          states[group.id] = false;
        } else {
          const checkedCount = groupPaths.filter((path) => getCheckboxState(path)).length;
          if (checkedCount === 0) states[group.id] = false;
          else if (checkedCount === groupPaths.length) states[group.id] = true;
          else states[group.id] = "indeterminate";
        }
      } else {
        states[group.id] = getGroupCheckedState(group.layerIds, checkedState);
      }
    });

    return states;
  }, [tab.groups, getCheckboxState, checkedState, tab.key]);

  const loadingIndicator = loadingState === TabLoadingState.LOADING && (
    <i className={clsx("text-lg", "leading-5", "icon-lt-loader-4-line", "animate-spin", "absolute")} />
  );

  const errorIndicator = loadingState === TabLoadingState.ERROR && (
    <Tooltip.Wrapper>
      <Tooltip.Trigger>
        <i className={clsx("text-lg", "leading-5", "icon-lt-alert-line")} />
      </Tooltip.Trigger>
      <Tooltip.Content align="center" side="bottom" theme={Theme.Dark} size="sm">
        <span className="whitespace-normal">{`Error loading ${t(tabTranslationKey(tab.key))} data`}</span>
      </Tooltip.Content>
    </Tooltip.Wrapper>
  );

  if (!tab.expandable) {
    if (tab.key === "topography") return null;
    return (
      <div className="flex items-center gap-2">
        <PrintableLayerControls
          layerId={tab.key}
          titleKey={tabTranslationKey(tab.key)}
          checked={tabCheckedState}
          onCheckedChange={() => handleToggleTab({ tabKey: tab.key })}
          indicator={loadingIndicator || errorIndicator}
        />
      </div>
    );
  }

  // Special case: If tab has only one group with extraControls, flatten the hierarchy
  const shouldFlattenHierarchy = tab.groups.length === 1 && tab.groups[0].extraControls;
  const singleGroup = shouldFlattenHierarchy ? tab.groups[0] : null;

  return (
    <div className="flex flex-col gap-2">
      <PrintableLayerControls
        layerId={tab.key}
        titleKey={tabTranslationKey(tab.key)}
        checked={tabCheckedState}
        onCheckedChange={() => handleToggleTab({ tabKey: tab.key })}
        indicator={loadingIndicator || errorIndicator}
        hideExpandButton={!!shouldFlattenHierarchy}
        {...(singleGroup && {
          sectionIds: singleGroup.sectionIds,
          sectionLabels: singleGroup.labels,
          hiddenSectionIds: singleGroup.sectionIds
            ? hiddenSectionIds(singleGroup.id, singleGroup.sectionIds)
            : undefined,
          toggleSection: singleGroup.sectionIds ? toggleSection(singleGroup.id) : undefined,
        })}
      >
        {singleGroup?.extraControls}
        {!shouldFlattenHierarchy &&
          tab.groups.map((group) => {
            const groupLayerIds = group.layerIds;
            const groupCheckedState = groupCheckedStates[group.id];

            if (group.sectionIds && group.sectionIds.length > 0) {
              return (
                <PrintableLayerControls
                  key={`${tab.key}-${group.id}`}
                  layerId={`${tab.key}-${group.id}`}
                  titleKey={group.titleKey}
                  checked={groupCheckedState}
                  onCheckedChange={() => handleToggleTab({ tabKey: tab.key, groupId: group.id })}
                  sectionIds={group.sectionIds}
                  sectionLabels={group.labels}
                  hiddenSectionIds={hiddenSectionIds(group.id, group.sectionIds)}
                  toggleSection={toggleSection(group.id)}
                  indicator={loadingIndicator || errorIndicator}
                >
                  {group.extraControls}
                </PrintableLayerControls>
              );
            }

            // Handle groups with extraControls and only one child for 2nd level items - render flat without expandable arrow
            const shouldRenderFlat = group.extraControls && groupLayerIds.length === 1;

            if (group.id === "comparables" || shouldRenderFlat) {
              if (groupLayerIds.length <= 1 || shouldRenderFlat) {
                return (
                  <div className="flex items-center gap-2" key={`${tab.key}-${group.id}`}>
                    <PrintableLayerControls
                      layerId={`${tab.key}-${group.id}`}
                      directTitle={t(group.titleKey)}
                      checked={groupCheckedState}
                      onCheckedChange={() => handleToggleTab({ tabKey: tab.key, groupId: group.id })}
                      hideExpandButton={!!shouldRenderFlat}
                    >
                      {group.extraControls}
                    </PrintableLayerControls>
                  </div>
                );
              }

              const hiddenLayerIds = groupLayerIds.filter((id) => !checkedState[id]);

              return (
                <PrintableLayerControls
                  key={`${tab.key}-${group.id}`}
                  layerId={`${tab.key}-${group.id}`}
                  directTitle={t(group.titleKey)}
                  checked={groupCheckedState}
                  onCheckedChange={() => handleToggleTab({ tabKey: tab.key, groupId: group.id })}
                  sectionIds={groupLayerIds}
                  sectionLabels={group.labels}
                  hiddenSectionIds={hiddenLayerIds}
                  toggleSection={(layerId) => handleToggleTab({ tabKey: tab.key, layerId })}
                >
                  {group.extraControls}
                </PrintableLayerControls>
              );
            }

            return (
              <div className="flex items-center gap-2" key={`${tab.key}-${group.id}`}>
                <PrintableLayerControls
                  layerId={`${tab.key}-${group.id}`}
                  directTitle={t(group.titleKey)}
                  checked={groupCheckedState}
                  onCheckedChange={() => handleToggleTab({ tabKey: tab.key, groupId: group.id })}
                >
                  {group.extraControls}
                </PrintableLayerControls>
              </div>
            );
          })}
      </PrintableLayerControls>
    </div>
  );
}
